import { Deserializer, makeQueryStringSerializer } from 'core/lib/serializer';
import * as OvationsApi from 'core/ovations-api';
import apiFetch from 'core/ovations-api/apiFetch';
import { API_URL } from 'core/ovations-api/constants';
import BatchStatus from 'core/ovations-api/enums/BatchStatus';

const baseUrl = (clientId: string, programId: string): string => {
    return `${API_URL}/api/v1/client/${clientId}/program/${programId}/Batch`;
};

export const defaultBatchCustomerSearchRequest: OvationsApi.Types.BatchCustomerSearchRequest = {
    page: 1,
};

const defaultRequest: Partial<OvationsApi.Types.BatchSearchRequest> = {
    page: 1,
    statuses: [
        BatchStatus.AwaitingApprovalForExport,
        BatchStatus.AwaitingDataControlApproval,
        BatchStatus.ExportApproved,
        BatchStatus.AwaitingFunding,
        BatchStatus.Created,
        BatchStatus.FundingApproved,
        BatchStatus.FundingReceived,
        BatchStatus.Sent,
        BatchStatus.VerifyingFunds,
    ],
};
const serializer = makeQueryStringSerializer<OvationsApi.Types.BatchSearchRequest>(defaultRequest, {
    page: Deserializer.Int,
});

export default class Batch {
    static defaultRequest = defaultRequest;

    static serializeRequest = serializer.serialize;

    static deserializeRequest = serializer.deserialize;

    static async fetchSingle(
        clientId: string,
        programId: string,
        batchId: string,
    ): Promise<OvationsApi.Types.Batch | undefined> {
        const url = `${baseUrl(clientId, programId)}/${batchId}`;
        const response = await apiFetch(url);
        const batch: OvationsApi.Types.Batch = await response.json();
        return batch;
    }

    static async fetchDetails(
        clientId: string,
        programId: string,
        batchId: string,
    ): Promise<OvationsApi.Types.BatchDetails | undefined> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/details`;
        const response = await apiFetch(url);
        const batchDetails: OvationsApi.Types.BatchDetails = await response.json();
        return batchDetails;
    }

    static async fetchAll(
        clientId: string,
        programId: string,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.Batch> | undefined> {
        const url = `${baseUrl(clientId, programId)}`;
        const response = await apiFetch(url);
        if (response.status !== 200) {
            return;
        }
        const json = await response.json();
        return json;
    }

    static async search(
        clientId: string,
        programId: string,
        batchesSearchRequest: OvationsApi.Types.BatchSearchRequest,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.Batch>> {
        const query = Batch.serializeRequest(batchesSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}?${query}`);
        const batches: OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.Batch> = await response.json();
        return batches;
    }

    static async cancel(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'DELETE',
            body: JSON.stringify(notes),
        });
    }

    static async approveFunds(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/approveFunds`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(notes),
        });
    }

    static async requestFunds(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/requestFunds`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(notes),
        });
    }

    static async approveExport(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/approveExport`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(notes),
        });
    }

    static async receiveFunds(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/fundsReceived`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(notes),
        });
    }

    static async send(clientId: string, programId: string, batchId: string, notes = ''): Promise<Response> {
        const url = `${baseUrl(clientId, programId)}/${batchId}/send`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(notes),
        });
    }

    static async searchCustomers(
        clientId: string,
        programId: string,
        batchId: string,
        batchCustomerSearchRequest: OvationsApi.Types.BatchCustomerSearchRequest,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchCustomer>> {
        const batchCustomerSerializer = makeQueryStringSerializer<OvationsApi.Types.BatchCustomerSearchRequest>(
            defaultBatchCustomerSearchRequest,
            { page: Deserializer.Int },
        );
        const query = batchCustomerSerializer.serialize(batchCustomerSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}/${batchId}/customerDetails?${query}`);
        const batchCustomers: OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchCustomer> =
            await response.json();
        return batchCustomers;
    }
}
